import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Flex,
  Button,
  useToast,
  Spacer,
  Progress,
  Text,
  Textarea,
  Select,
  HStack,
  IconButton,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { SettingsIcon, ChevronDownIcon } from '@chakra-ui/icons';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import ControlPanel from './ControlPanel';
import PromptManager from './PromptManager';
import WritingStyleModal from './WritingStyleModal';
import SelectionCommentMenu from './SelectionCommentMenu';
import Settings, { AISettings } from './Settings';
import { MultiAIApiClient, RewriteRequest, RewriteGenre, CustomPrompt, WritingStyle } from '../utils/aiApiClient';
import { historyManager } from '../utils/historyManager';
import { apiUsageManager } from '../utils/apiUsageManager';
import { useEditor } from '../context/EditorContext';

// デフォルトのAI設定
const defaultAISettings: AISettings = {
  provider: 'gemini-free',
  apiKeys: {}
};

// AI APIクライアントのインスタンスを作成
let aiClient = new MultiAIApiClient(defaultAISettings);

interface EditorProps {
  isPremium?: boolean;
}

const Editor: React.FC<EditorProps> = ({ isPremium = false }) => {
  const { editorState, setInputText: setContextInputText, setOutputText: setContextOutputText, markAsSaved } = useEditor();
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [commentMode, setCommentMode] = useState<boolean>(false);
  const [selectedGenre, setSelectedGenre] = useState<RewriteGenre>('general');
  const [currentCustomPrompt, setCurrentCustomPrompt] = useState<CustomPrompt | null>(null);
  const [writingStyles, setWritingStyles] = useState<WritingStyle[]>([]);
  const [selectedWritingStyle, setSelectedWritingStyle] = useState<WritingStyle | null>(null);
  const [editingWritingStyle, setEditingWritingStyle] = useState<WritingStyle | null>(null);
  const [aiSettings, setAiSettings] = useState<AISettings>(defaultAISettings);
  const [isEditingOutput, setIsEditingOutput] = useState<boolean>(false);

  // EditorContextとローカルstateを同期
  useEffect(() => {
    setInputText(editorState.inputText);
    setOutputText(editorState.outputText);
  }, [editorState.inputText, editorState.outputText]);

  // ローカルstateが変更されたときにEditorContextに反映
  const handleInputTextChange = (text: string) => {
    setInputText(text);
    setContextInputText(text);
  };

  const handleOutputTextChange = (text: string) => {
    setOutputText(text);
    setContextOutputText(text);
  };

  // AI設定の初期化
  useEffect(() => {
    // AI設定を読み込み
    const savedAISettings = localStorage.getItem('aiSettings');
    if (savedAISettings) {
      try {
        const settings = JSON.parse(savedAISettings);
        setAiSettings(settings);
        aiClient.updateSettings(settings);
      } catch (error) {
        console.error('Failed to load AI settings:', error);
      }
    }
  }, []);
  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  const toast = useToast();
  const { isOpen: isPromptManagerOpen, onOpen: onPromptManagerOpen, onClose: onPromptManagerClose } = useDisclosure();
  const { isOpen: isStyleModalOpen, onOpen: onStyleModalOpen, onClose: onStyleModalClose } = useDisclosure();
  const { isOpen: isSettingsOpen, onOpen: onSettingsOpen, onClose: onSettingsClose } = useDisclosure();
  
  // デバウンス用のタイマーID
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // コピー操作中かどうかのフラグ
  const isCopyingRef = useRef<boolean>(false);

  // ライティングスタイルをローカルストレージから読み込み
  useEffect(() => {
    const saved = localStorage.getItem('writingStyles');
    if (saved) {
      try {
        setWritingStyles(JSON.parse(saved));
      } catch (error) {
        console.error('Failed to load writing styles:', error);
      }
    }
  }, []);

  // リライト処理
  const handleRewrite = useCallback(async (comment?: string, genre?: RewriteGenre) => {
    if (!inputText.trim() || isProcessing) return;
    
    // レート制限をチェック
    const rateLimitCheck = apiUsageManager.checkRateLimit(aiSettings.provider);
    if (!rateLimitCheck.allowed) {
      toast({
        title: 'レート制限に達しました',
        description: rateLimitCheck.reason,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    setIsProcessing(true);
    
    try {
      const request: RewriteRequest = {
        text: inputText,
        comment,
        genre: genre || selectedGenre,
        customPrompt: currentCustomPrompt ? 
          `${currentCustomPrompt.prompt}${selectedWritingStyle ? `\n\n【ライティングスタイル指示】\n${selectedWritingStyle.instruction}` : ''}\n\n入力内容:\n${inputText}${comment ? `\n\n追加の指示:\n${comment}` : ''}` 
          : undefined,
        writingStyle: !currentCustomPrompt ? selectedWritingStyle?.instruction : undefined
      };
      
      // デバッグログ
      console.log('Request:', {
        provider: aiSettings.provider,
        customPrompt: !!currentCustomPrompt,
        writingStyle: !!selectedWritingStyle,
        finalWritingStyle: request.writingStyle,
        finalCustomPrompt: request.customPrompt
      });
      
      const response = await aiClient.rewrite(request);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      handleOutputTextChange(response.rewrittenText);
      
      // API使用量を記録
      if (response.tokenUsage) {
        apiUsageManager.recordUsage(
          aiSettings.provider,
          response.tokenUsage.input,
          response.tokenUsage.output
        );
      }
      
      // 履歴に保存
      try {
        const promptText = comment || 
          (currentCustomPrompt ? currentCustomPrompt.prompt : 
           (selectedWritingStyle ? `ライティングスタイル: ${selectedWritingStyle.name}` : 
            `ジャンル: ${genre || selectedGenre}`));
        
        const tags = [];
        if (currentCustomPrompt) tags.push('カスタムプロンプト');
        if (selectedWritingStyle) tags.push(`スタイル:${selectedWritingStyle.name}`);
        tags.push(`ジャンル:${genre || selectedGenre}`);
        
        historyManager.save(
          inputText,
          response.rewrittenText,
          promptText,
          aiSettings.provider,
          response.tokenUsage,
          tags
        );
      } catch (error) {
        console.error('Failed to save history:', error);
      }
      
      // API使用量を更新（実際の実装では）
      if (response.tokenUsage) {
        console.log('トークン使用量:', response.tokenUsage);
        // 実際の実装ではAPI使用量を更新する
      }
    } catch (error) {
      toast({
        title: 'エラーが発生しました',
        description: error instanceof Error ? error.message : '再度お試しください',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsProcessing(false);
    }
  }, [inputText, isProcessing, toast, selectedGenre, currentCustomPrompt, selectedWritingStyle, aiSettings.provider]);

  // 選択部分のリライト処理
  const handleSelectionRewrite = useCallback(async (
    selectedText: string, 
    comment: string, 
    startIndex: number, 
    endIndex: number
  ) => {
    if (isProcessing) return;
    
    // レート制限をチェック
    const rateLimitCheck = apiUsageManager.checkRateLimit(aiSettings.provider);
    if (!rateLimitCheck.allowed) {
      toast({
        title: 'レート制限に達しました',
        description: rateLimitCheck.reason,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    setIsProcessing(true);
    
    try {
      // 選択部分のみをリライト
      const request: RewriteRequest = {
        text: selectedText,
        comment: `選択部分を次のように修正してください: ${comment}`,
        genre: selectedGenre,
        customPrompt: undefined,
        writingStyle: selectedWritingStyle?.instruction
      };
      
      const response = await aiClient.rewrite(request);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      // 元のテキストの選択部分を置換
      const newText = inputText.slice(0, startIndex) + 
                     response.rewrittenText + 
                     inputText.slice(endIndex);
      
      handleInputTextChange(newText);
      
      // 全体を再リライト
      await handleRewrite();
      
      toast({
        title: '選択部分をリライトしました',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      
    } catch (error) {
      toast({
        title: '選択部分のリライトに失敗しました',
        description: error instanceof Error ? error.message : '再度お試しください',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsProcessing(false);
    }
  }, [inputText, isProcessing, toast, selectedGenre, selectedWritingStyle, aiSettings.provider, handleInputTextChange, handleRewrite]);

  // 保存処理
  const handleSave = useCallback(() => {
    if (!outputText.trim()) {
      toast({
        title: '保存できません',
        description: '保存するテキストがありません',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const promptText = currentCustomPrompt ? currentCustomPrompt.prompt : 
                        (selectedWritingStyle ? `ライティングスタイル: ${selectedWritingStyle.name}` : 
                         `ジャンル: ${selectedGenre}`);
      
      const tags = [];
      if (currentCustomPrompt) tags.push('カスタムプロンプト');
      if (selectedWritingStyle) tags.push(`スタイル:${selectedWritingStyle.name}`);
      tags.push(`ジャンル:${selectedGenre}`);
      tags.push('手動保存');
      
      historyManager.save(
        inputText || '手動入力',
        outputText,
        promptText,
        aiSettings.provider,
        undefined, // トークン使用量はマニュアル保存では不明
        tags
      );

      markAsSaved();
      
      toast({
        title: '保存しました',
        description: '履歴に保存されました',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: '保存に失敗しました',
        description: '再度お試しください',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [outputText, inputText, currentCustomPrompt, selectedWritingStyle, selectedGenre, aiSettings.provider, markAsSaved, toast]);

  // 入力テキストの変更を検知してリライト処理（デバウンス処理）
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // 履歴から読み込んだ場合やコピー操作中は自動リライトを無効化
    if (inputText.trim() && !isCopyingRef.current && !editorState.isEditingFromHistory) {
      timerRef.current = setTimeout(() => {
        handleRewriteRef.current();
      }, 1000);
    }

    // コピーフラグをリセット
    if (isCopyingRef.current) {
      isCopyingRef.current = false;
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [inputText, editorState.isEditingFromHistory]); // inputTextとhistory編集状態の変更時のみ実行
  
  // handleRewrite関数をuseRefで安定化
  const handleRewriteRef = useRef(handleRewrite);
  handleRewriteRef.current = handleRewrite;

  // 右側のリライト結果を左側のエディタにコピー
  const handleCopyToEditor = useCallback(() => {
    if (outputText) {
      // コピー操作中フラグを設定
      isCopyingRef.current = true;
      handleInputTextChange(outputText);
      
      toast({
        title: 'コピーしました',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    }
  }, [outputText, toast]);

  // コメントモード切替
  const toggleCommentMode = useCallback(() => {
    setCommentMode(prev => !prev);
    
    // コメントモードがオンになったら、次のレンダリング後にフォーカス
    if (!commentMode && commentInputRef.current) {
      setTimeout(() => {
        commentInputRef.current?.focus();
      }, 0);
    }
  }, [commentMode]);

  // コメント送信処理
  const handleCommentSubmit = useCallback((comment: string) => {
    if (!comment.trim()) return;
    
    toast({
      title: 'コメントを送信しました',
      description: 'リライト中...',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
    
    // コメント付きでリライト処理を実行
    handleRewrite(comment);
    
    // コメントモードを終了
    setCommentMode(false);
  }, [handleRewrite, toast]);

  // カスタムプロンプト選択時の処理
  const handleSelectCustomPrompt = useCallback((prompt: CustomPrompt) => {
    setCurrentCustomPrompt(prompt);
    setSelectedGenre(prompt.genre as RewriteGenre);
    onPromptManagerClose();
    
    // 既に入力がある場合は即座にリライト
    if (inputText.trim() && !isProcessing) {
      handleRewrite(undefined, prompt.genre as RewriteGenre);
    }
    
    toast({
      title: 'カスタムプロンプトを適用しました',
      description: prompt.name,
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  }, [inputText, isProcessing, handleRewrite, onPromptManagerClose, toast]);

  // ライティングスタイル管理
  const handleSaveWritingStyle = useCallback((style: WritingStyle) => {
    const existing = writingStyles.findIndex(s => s.id === style.id);
    let updatedStyles: WritingStyle[];
    
    if (existing >= 0) {
      updatedStyles = [...writingStyles];
      updatedStyles[existing] = style;
      // 編集中のスタイルが現在選択中の場合は更新
      if (selectedWritingStyle?.id === style.id) {
        setSelectedWritingStyle(style);
      }
    } else {
      updatedStyles = [...writingStyles, style];
    }
    
    setWritingStyles(updatedStyles);
    localStorage.setItem('writingStyles', JSON.stringify(updatedStyles));
    setEditingWritingStyle(null);
  }, [writingStyles, selectedWritingStyle]);

  const handleSelectWritingStyle = useCallback((style: WritingStyle | null) => {
    setSelectedWritingStyle(style);
    
    // 状態更新後にリライトを実行
    if (inputText.trim() && !isProcessing) {
      setTimeout(() => {
        handleRewriteRef.current();
      }, 0);
    }
    
    if (style) {
      toast({
        title: 'ライティングスタイルを適用しました',
        description: style.name,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    }
  }, [inputText, isProcessing, toast]);

  const handleEditWritingStyle = useCallback((style: WritingStyle) => {
    if (!isPremium) {
      toast({
        title: '有料プラン限定機能',
        description: 'ライティングスタイルの編集は有料プランでご利用いただけます',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setEditingWritingStyle(style);
    onStyleModalOpen();
  }, [isPremium, onStyleModalOpen, toast]);

  const handleDeleteWritingStyle = useCallback((styleId: string) => {
    if (!isPremium) {
      toast({
        title: '有料プラン限定機能',
        description: 'ライティングスタイルの削除は有料プランでご利用いただけます',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const updatedStyles = writingStyles.filter(s => s.id !== styleId);
    setWritingStyles(updatedStyles);
    localStorage.setItem('writingStyles', JSON.stringify(updatedStyles));

    // 削除したスタイルが現在選択中の場合はクリア
    if (selectedWritingStyle?.id === styleId) {
      setSelectedWritingStyle(null);
    }

    toast({
      title: 'ライティングスタイルを削除しました',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  }, [isPremium, writingStyles, selectedWritingStyle, toast]);

  const handleCreateNewStyle = useCallback(() => {
    if (!isPremium) {
      toast({
        title: '有料プラン限定機能',
        description: 'ライティングスタイルの作成は有料プランでご利用いただけます',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setEditingWritingStyle(null);
    onStyleModalOpen();
  }, [isPremium, onStyleModalOpen, toast]);

  const handleSaveAISettings = useCallback((settings: AISettings) => {
    setAiSettings(settings);
    aiClient.updateSettings(settings);
    
    toast({
      title: 'AI設定を更新しました',
      description: `プロバイダー: ${settings.provider}`,
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  }, [toast]);

  // カラーモード対応
  const editorBg = useColorModeValue('white', 'gray.800');
  const editorHeaderBg = useColorModeValue('gray.100', 'gray.700');
  const selectBg = useColorModeValue('white', 'gray.700');
  const promptInfoBg = useColorModeValue('blue.50', 'blue.900');
  const promptInfoColor = useColorModeValue('blue.800', 'blue.200');
  const styleInfoBg = useColorModeValue('green.50', 'green.900');
  const styleInfoColor = useColorModeValue('green.800', 'green.200');
  const markdownPreviewCodeBg = useColorModeValue('gray.100', 'gray.700');
  const counterBg = useColorModeValue('white', 'gray.800');

  return (
    <Box p={6}>
      <Flex 
        direction={{ base: 'column', md: 'row' }} 
        gap={4} 
        h={{ base: 'auto', md: '70vh' }}
      >
        {/* 左側: ユーザー入力エリア */}
        <Box 
          flex="1" 
          borderWidth="1px" 
          borderRadius="lg" 
          overflow="hidden"
          h={{ base: '40vh', md: 'full' }}
          bg={editorBg}
        >
          <Box p={2} bg={editorHeaderBg} borderBottomWidth="1px">
            <HStack>
              <Text fontWeight="bold">入力エリア</Text>
              <Spacer />
              <Select
                size="sm"
                width="150px"
                value={selectedGenre}
                onChange={(e) => {
                  const newGenre = e.target.value as RewriteGenre;
                  setSelectedGenre(newGenre);
                  setCurrentCustomPrompt(null); // 標準ジャンル選択時はカスタムプロンプトをクリア
                  // ジャンル変更時は即座にリライト実行（新しいジャンルを直接渡す）
                  if (inputText.trim() && !isProcessing) {
                    handleRewrite(undefined, newGenre);
                  }
                }}
                bg={selectBg}
              >
                <option value="general">一般</option>
                <option value="blog">ブログ記事</option>
                <option value="twitter">X (Twitter)</option>
                <option value="meeting">議事録</option>
                <option value="idea">アイデア整理</option>
                <option value="summary">要約</option>
              </Select>
              <IconButton
                aria-label="プロンプト管理"
                icon={<SettingsIcon />}
                size="sm"
                variant="ghost"
                onClick={onPromptManagerOpen}
                title="プロンプト管理"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={onSettingsOpen}
                title="AI設定"
              >
                {aiSettings.provider === 'gemini-free' ? 'Gemini' : 
                 aiSettings.provider === 'gemini' ? 'Gemini Pro' :
                 aiSettings.provider === 'claude' ? 'Claude' : 'GPT'}
              </Button>
              <Menu>
                <MenuButton
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                  size="sm"
                  variant="outline"
                  minW="140px"
                  maxW="200px"
                >
                  {selectedWritingStyle ? (
                    <Text fontSize="xs" noOfLines={1} isTruncated>
                      {selectedWritingStyle.name}
                    </Text>
                  ) : (
                    <Text fontSize="xs">スタイル選択</Text>
                  )}
                </MenuButton>
                <MenuList 
                  maxHeight="300px" 
                  overflowY="auto"
                  minWidth="280px"
                  maxWidth="400px"
                >
                  <MenuItem onClick={() => handleSelectWritingStyle(null)}>
                    <Text fontSize="sm">スタイルなし</Text>
                  </MenuItem>
                  {writingStyles.map(style => (
                    <MenuItem 
                      key={style.id} 
                      onClick={() => handleSelectWritingStyle(style)}
                      position="relative"
                      pr={isPremium ? 16 : 4}
                      whiteSpace="normal"
                      minHeight="60px"
                    >
                      <VStack align="start" spacing={0} flex={1}>
                        <Text fontSize="sm" fontWeight="bold">{style.name}</Text>
                        <Text fontSize="xs" color="gray.600" noOfLines={2} wordBreak="break-word">
                          {style.instruction}
                        </Text>
                      </VStack>
                      {isPremium && (
                        <HStack spacing={1} position="absolute" right={2}>
                          <Box
                            as="span"
                            px={1}
                            py={1}
                            borderRadius="sm"
                            cursor="pointer"
                            _hover={{ bg: 'gray.100' }}
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              handleEditWritingStyle(style);
                            }}
                            title="編集"
                          >
                            ✏️
                          </Box>
                          <Box
                            as="span"
                            px={1}
                            py={1}
                            borderRadius="sm"
                            cursor="pointer"
                            _hover={{ bg: 'red.100' }}
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              if (window.confirm('このライティングスタイルを削除してもよろしいですか？')) {
                                handleDeleteWritingStyle(style.id);
                              }
                            }}
                            title="削除"
                          >
                            🗑️
                          </Box>
                        </HStack>
                      )}
                    </MenuItem>
                  ))}
                  <MenuItem onClick={handleCreateNewStyle} isDisabled={!isPremium}>
                    <Text fontSize="sm" fontWeight="bold" color="blue.600">
                      + 新しいスタイルを作成
                    </Text>
                  </MenuItem>
                </MenuList>
              </Menu>
            </HStack>
            {currentCustomPrompt && (
              <Box bg={promptInfoBg} px={2} py={1} fontSize="xs" color={promptInfoColor}>
                カスタムプロンプト: {currentCustomPrompt.name}
              </Box>
            )}
            {selectedWritingStyle && (
              <Box bg={styleInfoBg} px={2} py={1} fontSize="xs" color={styleInfoColor}>
                ライティングスタイル: {selectedWritingStyle.name}
              </Box>
            )}
          </Box>
          <Box position="relative" h="calc(100% - 40px)">
            <Textarea
              value={inputText}
              onChange={(e) => handleInputTextChange(e.target.value)}
              placeholder="ここに思いついたことを自由に入力してください..."
              h="calc(100% - 25px)"
              border="none"
              resize="none"
              fontFamily="monospace"
              fontSize="14px"
              p={3}
              maxLength={30000}
              data-testid="input-textarea"
              _focus={{
                boxShadow: 'none',
                outline: 'none'
              }}
            />
            <Box 
              position="absolute" 
              bottom="5px" 
              right="10px" 
              fontSize="xs" 
              color={inputText.length > 25000 ? "red.500" : "gray.500"}
              bg={useColorModeValue('white', 'gray.800')}
              px={2}
              borderRadius="md"
            >
              {inputText.length.toLocaleString()} / 30,000
            </Box>
          </Box>
        </Box>
        
        {/* 右側: リライト結果エリア */}
        <Box 
          flex="1" 
          borderWidth="1px" 
          borderRadius="lg" 
          overflow="hidden"
          h={{ base: '40vh', md: 'full' }}
          position="relative"
          bg={editorBg}
        >
          <Box p={2} bg={editorHeaderBg} borderBottomWidth="1px">
            <Flex align="center">
              <Text fontWeight="bold">リライト結果</Text>
              <Spacer />
              {editorState.isEditingFromHistory && (
                <Text fontSize="sm" color="blue.500" mr={2}>
                  履歴から編集中
                  {editorState.hasUnsavedChanges && <Text as="span" color="orange.500">（未保存）</Text>}
                </Text>
              )}
              <Button 
                size="sm" 
                colorScheme="green" 
                onClick={handleSave}
                isDisabled={!outputText || isProcessing}
                mr={2}
              >
                保存
              </Button>
              <Button 
                size="sm" 
                colorScheme="blue" 
                onClick={handleCopyToEditor}
                isDisabled={!outputText || isProcessing}
              >
                左側にコピー
              </Button>
              <Button 
                size="sm" 
                ml={2} 
                onClick={() => setIsEditingOutput(!isEditingOutput)}
                variant={isEditingOutput ? "solid" : "outline"}
                colorScheme={isEditingOutput ? "purple" : "gray"}
                isDisabled={isProcessing || !outputText}
              >
                編集
              </Button>
              <Button 
                size="sm" 
                ml={2} 
                onClick={toggleCommentMode}
                variant={commentMode ? "solid" : "outline"}
                colorScheme={commentMode ? "orange" : "gray"}
                isDisabled={isProcessing}
              >
                コメント
              </Button>
            </Flex>
          </Box>
          
          {isProcessing && (
            <Progress size="xs" isIndeterminate colorScheme="blue" />
          )}
          
          {isEditingOutput ? (
            <Box position="relative" h="calc(100% - 40px)">
              <Textarea
                value={outputText}
                onChange={(e) => handleOutputTextChange(e.target.value)}
                placeholder="編集してください..."
                h="calc(100% - 25px)"
                border="none"
                resize="none"
                fontFamily="monospace"
                fontSize="14px"
                p={3}
                maxLength={30000}
                _focus={{
                  boxShadow: 'none',
                  outline: 'none'
                }}
              />
              <Box 
                position="absolute" 
                bottom="5px" 
                right="10px" 
                fontSize="xs" 
                color={outputText.length > 25000 ? "red.500" : "gray.500"}
                bg={counterBg}
                px={2}
                borderRadius="md"
              >
                {outputText.length.toLocaleString()} / 30,000
              </Box>
            </Box>
          ) : (
            <Box position="relative" h="calc(100% - 40px)">
              <Box 
                className="markdown-preview" 
                h="calc(100% - 25px)" 
                p={4} 
                overflowY="auto"
                sx={{
                  '& pre': {
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    backgroundColor: markdownPreviewCodeBg,
                    p: 3,
                    borderRadius: 'md',
                    fontSize: 'sm',
                  },
                  '& p': {
                    mb: 2,
                    lineHeight: 1.8,
                  },
                  '& ul, & ol': {
                    pl: 5,
                    mb: 2,
                  },
                  '& li': {
                    mb: 1,
                  }
                }}
              >
            {isProcessing ? (
              <VStack spacing={3} align="center" mt={8}>
                <Progress size="lg" isIndeterminate colorScheme="blue" w="300px" />
                <VStack spacing={1} align="center">
                  <Text color="gray.600" fontWeight="bold">AIがリライト中...</Text>
                  <Text color="gray.500" fontSize="sm">
                    長文の場合、複数回のAPI呼び出しで完全な応答を生成します
                  </Text>
                </VStack>
              </VStack>
            ) : outputText ? (
              <ReactMarkdown
                rehypePlugins={[rehypeRaw]}
                remarkPlugins={[remarkGfm]}
                components={{
                  p: ({ children }) => <Text mb={2} lineHeight={1.8}>{children}</Text>,
                  ul: ({ children }) => <Box as="ul" pl={5} mb={2}>{children}</Box>,
                  ol: ({ children }) => <Box as="ol" pl={5} mb={2}>{children}</Box>,
                  li: ({ children }) => <Box as="li" mb={1}>{children}</Box>,
                  h1: ({ children }) => <Text as="h1" fontSize="2xl" fontWeight="bold" mb={3}>{children}</Text>,
                  h2: ({ children }) => <Text as="h2" fontSize="xl" fontWeight="bold" mb={2}>{children}</Text>,
                  h3: ({ children }) => <Text as="h3" fontSize="lg" fontWeight="bold" mb={2}>{children}</Text>,
                  code: ({ inline, children }) => 
                    inline ? (
                      <Text as="code" bg={markdownPreviewCodeBg} px={1} fontSize="sm">{children}</Text>
                    ) : (
                      <Box as="pre" bg={markdownPreviewCodeBg} p={3} borderRadius="md" overflow="auto" mb={2}>
                        <Text as="code" fontSize="sm">{children}</Text>
                      </Box>
                    ),
                }}
              >
                {outputText}
              </ReactMarkdown>
            ) : (
              <Text color="gray.500">
                左側に文章を入力すると、AIがリアルタイムでリライトします
              </Text>
            )}
            
            {commentMode && (
              <Box className="comment-input" mt={4}>
                <Text fontSize="sm" mb={2}>AIへの指示を入力:</Text>
                <textarea
                  ref={commentInputRef}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #e2e8f0',
                  }}
                  rows={3}
                  maxLength={500}
                  placeholder="例: もっと簡潔にまとめて / 専門用語を追加して / 箇条書きにして"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.ctrlKey) {
                      handleCommentSubmit(e.currentTarget.value);
                      e.currentTarget.value = '';
                    }
                  }}
                />
                <Flex justify="space-between" align="center" mt={1}>
                  <Text fontSize="xs">Ctrl+Enterで送信</Text>
                  <Button 
                    size="xs" 
                    colorScheme="orange"
                    onClick={() => {
                      if (commentInputRef.current) {
                        handleCommentSubmit(commentInputRef.current.value);
                        commentInputRef.current.value = '';
                      }
                    }}
                  >
                    送信
                  </Button>
                </Flex>
              </Box>
            )}
              </Box>
              {outputText && (
                <Box 
                  position="absolute" 
                  bottom="5px" 
                  right="10px" 
                  fontSize="xs" 
                  color={outputText.length > 25000 ? "red.500" : "gray.500"}
                  bg={counterBg}
                  px={2}
                  borderRadius="md"
                >
                  {outputText.length.toLocaleString()} / 30,000
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Flex>
      
      <Box mt={4}>
        <ControlPanel markdownContent={outputText} />
      </Box>

      <PromptManager
        isOpen={isPromptManagerOpen}
        onClose={onPromptManagerClose}
        onSelectPrompt={handleSelectCustomPrompt}
        isPremium={isPremium}
      />

      <WritingStyleModal
        isOpen={isStyleModalOpen}
        onClose={onStyleModalClose}
        onSave={handleSaveWritingStyle}
        editingStyle={editingWritingStyle}
      />

      <Settings
        isOpen={isSettingsOpen}
        onClose={onSettingsClose}
        onSave={handleSaveAISettings}
      />
      
      <SelectionCommentMenu
        onComment={handleSelectionRewrite}
        isProcessing={isProcessing}
      />
    </Box>
  );
};

export default Editor;
